import { useEffect, useRef, useState } from "react";
import { PushPin, CaretDown, Truck, Wrench, PaintBrush, CloudRain, Warning, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

/** Reopen a completed WO as a Follow-Up + pin it to a truck in one flow.
 *  Written specifically for the "this paint job needs a touch-up — put it on
 *  Alejandro's radar for tomorrow" case, where the office had to (1) reopen,
 *  (2) set follow-up reason, (3) tap Pin, (4) pick tech, (5) pick date. Now
 *  it's one dropdown with three taps.
 */
const isoOffset = (n) => {
  const d = new Date(); d.setDate(d.getDate() + n);
  const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const REASONS = [
  { key: "paint_touch_up",    label: "Paint Touch-Up",   Icon: PaintBrush, bg: "bg-fuchsia-600 hover:bg-fuchsia-700 text-white" },
  { key: "roof_storm_verify", label: "Storm / Rain Verify", Icon: CloudRain,  bg: "bg-sky-600 hover:bg-sky-700 text-white" },
  { key: "other",             label: "Other",            Icon: Warning,    bg: "bg-yellow-500 hover:bg-yellow-600 text-slate-900" },
];

export default function ReopenAndPinButton({ jobId, workers, onDone }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("reason"); // reason → truck
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(isoOffset(0));
  const [subs, setSubs] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) { setStep("reason"); setReason(""); return; }
    api.get("/sub-contractors").then(r => setSubs((r.data || []).filter(s => s.sweeper_specialty || s.trade))).catch(() => {});
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pickReason = (r) => { setReason(r); setStep("truck"); };

  const run = async (truck_key, truck_label) => {
    setBusy(true);
    try {
      // 1. Reopen as follow-up with the chosen reason
      await api.post(`/jobs/${jobId}/reopen`, { as: "follow_up", follow_up_reason: reason });
      // 2. Pin to the chosen truck + date
      const r = await api.post("/jobs/sweeper-pin", { job_ids: [jobId], truck_key, truck_label, date });
      if (r.data.pinned === 0) {
        toast.error(r.data.reason || "Reopened but couldn't pin — try again from the Pin button");
      } else {
        const reasonLabel = REASONS.find(x => x.key === reason)?.label || "Follow-Up";
        const dateLabel = date === isoOffset(0) ? "today" : date === isoOffset(1) ? "tomorrow" : date;
        toast.success(`↺ Reopened (${reasonLabel}) · 📌 ${truck_label} · ${dateLabel}`);
        onDone?.();
      }
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not reopen"));
    } finally { setBusy(false); }
  };

  const techs = (workers || []).filter(w => w.role === "worker");

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)} disabled={busy}
        data-testid="reopen-and-pin-btn"
        className="h-10 px-3 bg-gradient-to-r from-amber-400 to-fuchsia-500 text-white border-2 border-slate-900 font-black uppercase text-xs tracking-wider inline-flex items-center gap-1.5 hover:brightness-110 disabled:opacity-60 shadow-[3px_3px_0_#0f172a]">
        <ArrowClockwise weight="bold" size={14}/> Reopen &amp; Pin <CaretDown weight="bold" size={10}/>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-40 w-72 bg-white border-2 border-slate-900 shadow-[6px_6px_0_#0f172a] p-3" data-testid="reopen-pin-menu">
          {step === "reason" && (
            <>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-2">
                Why reopen? (Follow-Up reason)
              </div>
              <div className="flex flex-col gap-1.5">
                {REASONS.map(r => (
                  <button key={r.key} onClick={() => pickReason(r.key)} disabled={busy}
                    data-testid={`reopen-reason-${r.key}`}
                    className={`w-full text-left h-11 px-2.5 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-2 ${r.bg}`}>
                    <r.Icon weight="fill" size={16}/> {r.label}
                  </button>
                ))}
              </div>
              <div className="mt-3 text-[10px] text-slate-500 leading-tight">
                Reopens the WO with this Follow-Up reason, then lets you pick which truck runs it.
              </div>
            </>
          )}
          {step === "truck" && (
            <>
              <div className="flex items-center justify-between mb-2">
                <button onClick={() => setStep("reason")} data-testid="reopen-back-to-reason"
                  className="text-[10px] font-bold text-slate-500 hover:text-slate-900">← back</button>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Pick truck</span>
              </div>
              <div className="flex gap-1 mb-2" data-testid="reopen-date-toggle">
                {[{ key: isoOffset(0), label: "Today" }, { key: isoOffset(1), label: "Tomorrow" }].map(d => (
                  <button key={d.key} onClick={() => setDate(d.key)} data-testid={`reopen-date-${d.label.toLowerCase()}`}
                    className={`flex-1 h-8 border-2 text-[10px] font-black uppercase tracking-widest ${date === d.key ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white text-slate-600 border-slate-300"}`}>
                    {d.label}
                  </button>
                ))}
              </div>
              <div className="max-h-56 overflow-y-auto border border-slate-200">
                {techs.map(w => (
                  <button key={w.id} onClick={() => run(w.id, w.name)} disabled={busy} data-testid={`reopen-pin-truck-${w.id}`}
                    className="w-full text-left h-9 px-2 text-xs font-bold hover:bg-amber-50 inline-flex items-center gap-2 border-b border-slate-100">
                    <Truck weight="fill" size={14} className="text-slate-500"/> {w.name}
                  </button>
                ))}
                {subs.map(s => (
                  <button key={s.id} onClick={() => run(`sub:${s.id}`, s.name)} disabled={busy} data-testid={`reopen-pin-truck-sub-${s.id}`}
                    className="w-full text-left h-9 px-2 text-xs font-bold hover:bg-fuchsia-50 inline-flex items-center gap-2 border-b border-slate-100">
                    <Wrench weight="fill" size={14} className="text-fuchsia-600"/> {s.name}
                    <span className="ml-auto text-[9px] uppercase text-fuchsia-600">sub</span>
                  </button>
                ))}
                {techs.length === 0 && subs.length === 0 && <div className="p-2 text-xs text-slate-500">No trucks available</div>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
